import { useMemo } from 'react';
import { Spinner } from '@ds';
import { RouteProvider } from '@lp/core/contexts/RouteContext';
import { AircraftUtilizationProvider } from '@lp/features/RoutePlanner/contexts/AircraftUtilizationContext';
import { getRoutesByTransport } from '@lp/features/MultiSuggest/utils/route';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { CaseResourceType, ServiceType } from '@/api/apis/case';
import { LegLocationTypeEnum, RouteTypeEnum } from '@/api/apis/route';
import { MultiSuggestRoutePlanType } from '@/utils/route/chains';
import { DataCacheType } from '@/queries/consts';
import { RouteList } from './RouteList';
import { ResourcesList } from './ResourcesList';
import { SuggestedProviderTimeValue } from './SuggestedProviderTimeValue';
import { ListEmptyState } from '../ListEmptyState';
import { SuggestInfoWarnings } from './SuggestInfoWarnings';
import { AccommodationsList } from './AccommodationsList';

type SuggestInfoProps = {
  serviceType: ServiceType;
  data?: MultiSuggestRoutePlanType | null;
  isLoading: boolean;
};

export const SuggestInfo = ({ serviceType, data, isLoading }: SuggestInfoProps) => {
  const { legs: logisticsLegs } = useRouteDetails(RouteTypeEnum.Main);
  const { list } = useRouteLocations(DataCacheType.Snapshot);

  const isSurgeonRequired = serviceType === ServiceType.NopWithTmdxSurgeon;
  const suggestedLegs = useMemo(() => (data ? Array.from(data.legs.values()).flat() : []), [data]);
  const suggestedAccommodationLegs = useMemo(
    () => (data ? Array.from(data.accommodationLegs.values()).flat() : []),
    [data],
  );

  const routeList = useMemo(() => {
    const aircrafts = data?.resources[CaseResourceType.Aircraft].map((i) => ({
      resourceId: i.resourceId,
      resourceType: i.resourceType,
      tailNumber: i.data!.registration,
    }));

    const legs = suggestedLegs.toSorted(
      (a, b) => new Date(a.departureTime).getTime() - new Date(b.departureTime).getTime(),
    );

    return getRoutesByTransport({ legs, aircrafts: aircrafts || [], isSuggested: true });
  }, [data, suggestedLegs]);

  const locations = useMemo(() => {
    const mainLocations = list.filter((l) =>
      [LegLocationTypeEnum.hospital, LegLocationTypeEnum.txcenter].includes(l.locationType),
    );

    return [...(data?.locations || []), ...mainLocations];
  }, [data?.locations, list]);

  if (isLoading) {
    return (
      <div className="flex flex-col p-4 gap-4 bg-white">
        <span className="text-m-cps text-base-30">Calculation...</span>
        <div className="w-full h-full py-16 flex items-center justify-center">
          <Spinner size="md" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col p-4 gap-4 bg-white">
        <ListEmptyState iconBgClass="bg-base-70" iconColorClass="text-base-40" message="Nothing found" />
      </div>
    );
  }

  return (
    <RouteProvider locations={locations} resources={data.resources}>
      <AircraftUtilizationProvider>
        <div className="flex flex-col p-4 gap-4 bg-white">
          <SuggestedProviderTimeValue title="SUGGESTED" currentLegs={logisticsLegs} suggestedLegs={suggestedLegs} />
          <RouteList list={routeList} />
          <AccommodationsList legs={suggestedAccommodationLegs} />
          <ResourcesList
            isSurgeonRequired={isSurgeonRequired}
            ocsWarning={data.ocsWarning}
            specialistWarning={data.specialistWarning}
            isSuggestedRoute
          />
          <SuggestInfoWarnings data={data} />
        </div>
      </AircraftUtilizationProvider>
    </RouteProvider>
  );
};
